import { sendJson } from './transport.js';

export class RealtimeRoomController {
  constructor({
    createSocket = url => new WebSocket(url),
    onStatus = () => {},
    onMessage = () => {},
    onError = () => {},
    onReconnectNeeded = () => {},
    pingMessage = null,
    reconnectDelayMs = 2000,
    pingIntervalMs = 25000
  } = {}) {
    this.createSocket = createSocket;
    this.onStatus = onStatus;
    this.onMessage = onMessage;
    this.onError = onError;
    this.onReconnectNeeded = onReconnectNeeded;
    this.pingMessage = pingMessage;
    this.reconnectDelayMs = reconnectDelayMs;
    this.pingIntervalMs = pingIntervalMs;

    this.socket = null;
    this.allowReconnect = false;
    this.isReconnecting = false;
    this.reconnectTimer = null;
    this.pingTimer = null;
  }

  get isOpen() {
    return Boolean(this.socket && this.socket.readyState === 1);
  }

  connect({ url, helloMessage, reconnecting = this.isReconnecting }) {
    this.disconnect({ notify: false, allowReconnect: false });
    this.allowReconnect = true;
    this.onStatus(reconnecting ? 'reconnecting' : 'connecting');

    let socket;
    try {
      socket = this.createSocket(url);
      this.socket = socket;
    } catch {
      this.onStatus('disconnected');
      this.onError('Không thể kết nối máy chủ trực tuyến.');
      return false;
    }

    socket.addEventListener('open', () => {
      if (this.socket !== socket) return;
      this.isReconnecting = false;
      this.onStatus('connected');
      if (helloMessage) this.send(helloMessage);
      this.startPing();
    });

    socket.addEventListener('message', event => {
      if (this.socket !== socket) return;
      try {
        this.onMessage(JSON.parse(event.data));
      } catch {
        // Ignore malformed frames; authoritative server messages are JSON.
      }
    });

    socket.addEventListener('close', () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.stopPing();
      this.onStatus('disconnected');
      if (this.allowReconnect) this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      if (this.socket !== socket) return;
      this.onStatus('disconnected');
      this.onError('Lỗi kết nối máy chủ.');
    });

    return true;
  }

  disconnect({ notify = true, allowReconnect = false } = {}) {
    this.allowReconnect = allowReconnect;
    this.stopPing();
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.isReconnecting = false;

    const socket = this.socket;
    this.socket = null;
    if (socket) {
      try {
        socket.close();
      } catch {
        // Ignore close failures during teardown.
      }
    }

    if (notify) this.onStatus('disconnected');
  }

  send(message) {
    return sendJson(this.socket, message);
  }

  startPing() {
    this.stopPing();
    if (!this.pingMessage) return;
    this.pingTimer = setInterval(() => this.send(this.pingMessage), this.pingIntervalMs);
  }

  stopPing() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  scheduleReconnect() {
    this.isReconnecting = true;
    clearTimeout(this.reconnectTimer);
    this.onStatus('reconnecting');
    this.reconnectTimer = setTimeout(() => {
      if (!this.allowReconnect) return;
      this.onReconnectNeeded();
    }, this.reconnectDelayMs);
  }

  reconnectNow() {
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.isReconnecting = false;
    this.onReconnectNeeded();
  }

  suppressReconnect() {
    this.allowReconnect = false;
    clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  markOffline() {
    this.onStatus('disconnected');
  }
}
